import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 * @property {HTMLElement} [savingsBadge]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener(ThemeEvents.variantSelected, this.updatePriceOptimistically);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener(ThemeEvents.variantSelected, this.updatePriceOptimistically);
  }

  /**
   * Optimistically paints the price the instant a variant is selected, using
   * real Shopify data already embedded on the selected option (only present
   * for single-option products -- see snippets/variant-main-picker.liquid).
   * This is a visual head start only: updatePrice() (driven by the
   * authoritative section fetch response) always runs afterward and
   * reconciles/replaces this DOM, exactly as before. No price is ever
   * invented -- if the event doesn't carry reliable price data (multi-option
   * products), this is a no-op and the UI simply waits for the server
   * response as it always has.
   * @param {CustomEvent<{resource: {price?: number, compareAtPrice?: number}}>} event
   */
  updatePriceOptimistically = (event) => {
    const { price, compareAtPrice } = event.detail.resource ?? {};
    if (typeof price !== 'number' || Number.isNaN(price)) return;

    const useCurrency = this.dataset.useCurrency === 'true';
    const format = useCurrency ? this.dataset.moneyWithCurrencyFormat : this.dataset.moneyFormat;
    const currency = this.dataset.currency;
    if (!format || !currency) return;

    const { priceContainer, savingsBadge } = this.refs;
    if (!priceContainer) return;

    // Once `price` is known, this option carries trustworthy real Shopify
    // data (see snippets/variant-main-picker.liquid), so a missing/blank
    // compare-at-price is a definitive "not on sale" (0), not an unknown --
    // price, compare-at visibility, color, and the SAVE badge must all move
    // together as one atomic update, never partially waiting on the server.
    const normalizedCompareAtPrice = typeof compareAtPrice === 'number' && !Number.isNaN(compareAtPrice)
      ? compareAtPrice
      : 0;
    const showCompare = normalizedCompareAtPrice > price;
    const formattedPrice = formatMoney(price, format, currency);

    const regularEl = priceContainer.querySelector('.price__regular');
    const saleEl = priceContainer.querySelector('.price__sale');
    const regularPriceEl = regularEl?.querySelector('.price');
    const salePriceEl = saleEl?.querySelector('.price-item--sale.price');
    const compareEl = saleEl?.querySelector('.price-item--regular.compare-at-price');

    if (regularPriceEl) regularPriceEl.textContent = formattedPrice;
    if (salePriceEl) salePriceEl.textContent = formattedPrice;
    if (compareEl && showCompare) {
      compareEl.textContent = formatMoney(normalizedCompareAtPrice, format, currency);
    }

    regularEl?.classList.toggle('price__hidden', showCompare);
    saleEl?.classList.toggle('price__hidden', !showCompare);

    const regularColor = this.dataset.regularColor;
    const saleColor = this.dataset.saleColor;
    if (regularColor && saleColor) {
      this.style.setProperty('--paw-price-current-color', showCompare ? saleColor : regularColor);
    }

    if (savingsBadge) {
      if (showCompare) {
        const savingsAmount = normalizedCompareAtPrice - price;
        const savingsPercent = Math.floor((savingsAmount * 100) / normalizedCompareAtPrice);
        const prefix = this.dataset.savingsPrefix ?? '';
        const mode = this.dataset.savingsMode;
        const formattedSavings = formatMoney(savingsAmount, format, currency);

        let text;
        if (mode === 'amount') text = `${prefix} ${formattedSavings}`;
        else if (mode === 'percentage') text = `${prefix} ${savingsPercent}%`;
        else text = `${prefix} ${formattedSavings} (${savingsPercent}%)`;

        savingsBadge.textContent = text.trim();
        savingsBadge.hidden = false;
      } else {
        savingsBadge.hidden = true;
      }
    }
  };

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote, savingsBadge } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Some configurable Price modules (blocks/product-price-card.liquid) drive
    // colors (e.g. regular vs. sale price color) from a CSS custom property on
    // this element's own style attribute, recomputed per-variant server-side --
    // that lives outside any [ref] child, so it needs its own sync here.
    const newStyle = newProductPrice.getAttribute('style');
    if (newStyle != null) {
      this.setAttribute('style', newStyle);
    }

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update savings badge (blocks/product-price-card.liquid only -- absent
    // elsewhere, so this is a safe no-op for every other product-price usage).
    const newBadge = newProductPrice.querySelector('[ref="savingsBadge"]');

    if (!newBadge) {
      savingsBadge?.remove();
    } else if (!savingsBadge) {
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newBadge.cloneNode(true)));
    } else {
      savingsBadge.replaceWith(newBadge);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
